import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../services/api';

interface User {
  id: string;
  email: string;
  settings?: any;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (token) {
          api.setAuthToken(token);
          const response = await api.auth.getProfile();
          setUser({
            id: response.user.id,
            email: response.user.email,
            settings: response.settings
          });
        }
      } catch (error) {
        // Token is invalid or expired
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    const response = await api.auth.login({ email, password });
    
    localStorage.setItem('access_token', response.access_token);
    localStorage.setItem('refresh_token', response.refresh_token);
    api.setAuthToken(response.access_token);
    
    // Get user profile after login
    const profile = await api.auth.getProfile();
    setUser({
      id: profile.user.id,
      email: profile.user.email,
      settings: profile.settings
    });
  };

  const signup = async (email: string, password: string) => {
    const response = await api.auth.signup({ 
      email, 
      password,
      sync_enabled: true,
      analytics_enabled: false
    });
    
    localStorage.setItem('access_token', response.access_token);
    localStorage.setItem('refresh_token', response.refresh_token);
    api.setAuthToken(response.access_token);
    
    // Get user profile after signup
    const profile = await api.auth.getProfile();
    setUser({
      id: profile.user.id,
      email: profile.user.email,
      settings: profile.settings
    });
  };

  const logout = async () => {
    try {
      await api.auth.logout();
    } catch (error) {
      // Ignore errors on logout
    }
    
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    api.setAuthToken(null);
    setUser(null);
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  const value = {
    user,
    loading,
    login,
    signup,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};